/**
 * Lead-side derivation and functional landmark labelling for the Trace Pointes
 * landmark chips.
 *
 * Context
 * -------
 * When a mirrored eval occurs (right-lead template vs left-lead athlete), the
 * backend scores the athlete in the TEMPLATE's coordinate frame.  The landmark
 * chips inside the MultiTrailSelector show anatomical names (L Wrist / R Wrist),
 * which caused a coach to compare the template's R Wrist (lead/attacking arm)
 * against the athlete's R Wrist (trailing arm) and draw wrong conclusions.
 *
 * Fix: when the panel's lead side is known, replace anatomical side labels with
 * functional ones ("Lead Wrist (L)", "Trail Wrist (R)") so the coach immediately
 * understands which arm is which.
 *
 * Derivation rules
 * ----------------
 * Input: evaluation.metadata.mirroring_decision
 *   - should_mirror: bool
 *   - declared.template_lead_side   / detected.template_lead_side
 *   - declared.lead_side_override   / declared.athlete_handedness
 *   - detected.athlete_lead_side
 *
 * Priority (per CLAUDE.md Declarative Mirroring):
 *   Declared > Detected > null
 *   'unknown' (from detector) → treated as null
 *
 * Cross-side inference from should_mirror:
 *   When should_mirror=true  → one side is the OPPOSITE of the other.
 *   When should_mirror=false → both sides are the SAME.
 *
 * Each panel uses its OWN lead side.  Template panel gets `templateLeadSide`;
 * athlete panel gets `athleteLeadSide`.
 *
 * Labelling
 * ---------
 * Paired joints (Shoulder/Elbow/Wrist/Hip/Knee/Ankle):
 *   lead_side 'left'  → index 11,13,15,23,25,27 = "Lead <Joint> (L)"
 *                      → index 12,14,16,24,26,28 = "Trail <Joint> (R)"
 *   lead_side 'right' → index 12,14,16,24,26,28 = "Lead <Joint> (R)"
 *                      → index 11,13,15,23,25,27 = "Trail <Joint> (L)"
 * Single joint: index 0 = "Nose (Head)" always.
 *
 * When lead side is null, all labels stay anatomical (byte-identical to the old
 * UI).
 *
 * Smart default
 * -------------
 * `pickLeadSideDefaults` returns [leadWrist, leadAnkle] for a known lead side,
 * visibility-gated the same way as the old [16, 28] default.
 */

export type LeadSide = 'left' | 'right' | null;

// MediaPipe landmark indices for reference.
const LM_LEFT_SHOULDER  = 11;
const LM_RIGHT_SHOULDER = 12;
const LM_LEFT_ELBOW     = 13;
const LM_RIGHT_ELBOW    = 14;
const LM_LEFT_WRIST     = 15;
const LM_RIGHT_WRIST    = 16;
const LM_LEFT_HIP       = 23;
const LM_RIGHT_HIP      = 24;
const LM_LEFT_KNEE      = 25;
const LM_RIGHT_KNEE     = 26;
const LM_LEFT_ANKLE     = 27;
const LM_RIGHT_ANKLE    = 28;

/** Map from landmark index → base joint name (no side). */
const JOINT_BASE: Record<number, string> = {
  [LM_LEFT_SHOULDER]:  'Shoulder',
  [LM_RIGHT_SHOULDER]: 'Shoulder',
  [LM_LEFT_ELBOW]:     'Elbow',
  [LM_RIGHT_ELBOW]:    'Elbow',
  [LM_LEFT_WRIST]:     'Wrist',
  [LM_RIGHT_WRIST]:    'Wrist',
  [LM_LEFT_HIP]:       'Hip',
  [LM_RIGHT_HIP]:      'Hip',
  [LM_LEFT_KNEE]:      'Knee',
  [LM_RIGHT_KNEE]:     'Knee',
  [LM_LEFT_ANKLE]:     'Ankle',
  [LM_RIGHT_ANKLE]:    'Ankle',
};

/** MediaPipe "left" side landmark indices. */
const LEFT_INDICES = new Set([
  LM_LEFT_SHOULDER, LM_LEFT_ELBOW, LM_LEFT_WRIST,
  LM_LEFT_HIP, LM_LEFT_KNEE, LM_LEFT_ANKLE,
]);

/**
 * Generate the functional chip label for a landmark given the panel's lead side.
 *
 * When leadSide is null, returns the original anatomical label unchanged.
 *
 * @param landmarkIndex  MediaPipe landmark index
 * @param anatomicalLabel  Existing label string (e.g. "R Wrist")
 * @param leadSide  The panel's lead side ('left' | 'right' | null)
 */
export function functionalChipLabel(
  landmarkIndex: number,
  anatomicalLabel: string,
  leadSide: LeadSide,
): string {
  // Index 0 is always "Nose (Head)" regardless of lead side.
  if (landmarkIndex === 0) return anatomicalLabel;
  if (leadSide === null) return anatomicalLabel;

  const base = JOINT_BASE[landmarkIndex];
  if (!base) return anatomicalLabel; // Unknown landmark — fall back.

  const isLeft = LEFT_INDICES.has(landmarkIndex);

  // Determine whether this landmark is on the lead or trail side.
  const isLead = leadSide === 'left' ? isLeft : !isLeft;

  // Anatomical hint: the physical side letter.
  const sideHint = isLeft ? 'L' : 'R';

  return isLead
    ? `Lead ${base} (${sideHint})`
    : `Trail ${base} (${sideHint})`;
}

/**
 * Raw mirroring_decision shape from the backend evaluation metadata.
 * Only the fields we care about; unknown fields are safely ignored.
 */
export interface MirroringDecision {
  should_mirror?: boolean;
  declared?: {
    template_lead_side?: string | null;
    lead_side_override?: string | null;
    athlete_handedness?: string | null;
  };
  detected?: {
    template_lead_side?: string | null;
    athlete_lead_side?: string | null;
  };
}

/** Coerce a raw string from the backend to LeadSide (treats 'unknown' as null). */
function toLeadSide(raw: string | null | undefined): LeadSide {
  if (raw === 'left' || raw === 'right') return raw;
  return null;
}

/**
 * Derive templateLeadSide and athleteLeadSide from a mirroring_decision object.
 *
 * Priority:
 *   declared > detected > null
 *   'unknown' is treated as null in both tiers.
 *
 * Cross-side inference:
 *   When should_mirror=true  → sides are OPPOSITE (left ↔ right).
 *   When should_mirror=false → sides are the SAME.
 *
 * Returns { templateLeadSide, athleteLeadSide } — both null when unknown.
 *
 * Derivation truth table (should_mirror / T known / A known → result):
 *
 *   mirror=true,  T=right, A=?    → A=left   (opposite)
 *   mirror=true,  T=?,     A=left → T=right  (opposite)
 *   mirror=true,  T=right, A=left → no change needed (consistent)
 *   mirror=false, T=left,  A=?    → A=left   (same)
 *   mirror=false, T=?,     A=right→ T=right  (same)
 *   mirror=null,  T=right, A=?    → A=null   (no inference)
 *   mirror=null,  T=?,     A=left → T=null   (no inference)
 *
 * Note: should_mirror=undefined → no inference is safe.
 */
export function deriveLeadSides(
  decision: MirroringDecision | null | undefined,
): { templateLeadSide: LeadSide; athleteLeadSide: LeadSide } {
  if (!decision) {
    return { templateLeadSide: null, athleteLeadSide: null };
  }

  // Derive template lead side: declared > detected.
  let templateLeadSide: LeadSide = toLeadSide(decision.declared?.template_lead_side);
  if (templateLeadSide === null) {
    templateLeadSide = toLeadSide(decision.detected?.template_lead_side);
  }

  // Derive athlete lead side:
  //   declared.lead_side_override takes top priority,
  //   then declared.athlete_handedness,
  //   then detected.athlete_lead_side.
  let athleteLeadSide: LeadSide = toLeadSide(decision.declared?.lead_side_override);
  if (athleteLeadSide === null) {
    athleteLeadSide = toLeadSide(decision.declared?.athlete_handedness);
  }
  if (athleteLeadSide === null) {
    athleteLeadSide = toLeadSide(decision.detected?.athlete_lead_side);
  }

  // Cross-side inference — ONLY when should_mirror === true.
  //
  // should_mirror=true is a POSITIVE determination that the sides differ
  // (the pipeline decided to mirror), so the unknown side is the opposite.
  //
  // should_mirror=false is AMBIGUOUS and must NOT infer: it can mean
  // "sides are the same" OR "the confidence gate declined to decide"
  // (layer2/layer3 low-confidence paths) — in the latter case the sides may
  // actually differ, and inferring "same" would mislabel the chips.
  // Unknown stays unknown → anatomical labels (safe fallback).
  if (decision.should_mirror === true) {
    const opposite = (s: LeadSide): LeadSide =>
      s === 'left' ? 'right' : s === 'right' ? 'left' : null;
    if (templateLeadSide !== null && athleteLeadSide === null) {
      athleteLeadSide = opposite(templateLeadSide);
    } else if (athleteLeadSide !== null && templateLeadSide === null) {
      templateLeadSide = opposite(athleteLeadSide);
    }
  }

  return { templateLeadSide, athleteLeadSide };
}

/**
 * Lead wrist index for a given lead side (or null when unknown).
 * Returns null → caller falls back to standard [16, 28] logic.
 */
export function leadWristIndex(leadSide: LeadSide): number | null {
  if (leadSide === 'left')  return LM_LEFT_WRIST;
  if (leadSide === 'right') return LM_RIGHT_WRIST;
  return null;
}

/**
 * Lead ankle index for a given lead side (or null when unknown).
 */
export function leadAnkleIndex(leadSide: LeadSide): number | null {
  if (leadSide === 'left')  return LM_LEFT_ANKLE;
  if (leadSide === 'right') return LM_RIGHT_ANKLE;
  return null;
}

/**
 * Return the ordered pair [leadWrist, leadAnkle] when the lead side is known AND
 * both landmarks clear the visibility floor.  Returns null when lead side is null
 * or the standard [16, 28] default already covers this case (lead side = right).
 *
 * Callers should only use this when the user hasn't touched the selection yet
 * (respecting the athleteTrailTouched / templateTrailTouched refs).
 *
 * @param leadSide  The panel's lead side.
 * @param meanVis   Per-landmark mean visibility map from meanVisibilityByLandmark().
 * @param visFloor  Minimum mean visibility to consider a landmark "visible".
 */
export function pickLeadSideDefaults(
  leadSide: LeadSide,
  meanVis: Map<number, number>,
  visFloor: number,
): number[] | null {
  if (leadSide === null) return null;
  // Right-lead: lead wrist=16, lead ankle=28 — same as the existing default [16, 28].
  // Only return a special value for LEFT lead (or when right-lead but ankle low-vis,
  // which pickVisibleTrailDefaults already handles).
  const wrist = leadWristIndex(leadSide);
  const ankle = leadAnkleIndex(leadSide);
  if (wrist === null || ankle === null) return null;

  // If both are visible, return them in lead-first order.
  // For right-lead this would duplicate the [16,28] default → pickVisibleTrailDefaults
  // already handles that case, so we only override when lead side is LEFT (different).
  const wristVis = meanVis.size > 0 ? (meanVis.get(wrist) ?? 0) : 1;
  const ankleVis = meanVis.size > 0 ? (meanVis.get(ankle) ?? 0) : 1;
  if (wristVis >= visFloor && ankleVis >= visFloor) {
    return [wrist, ankle];
  }
  // One or both are not visible — let the caller's existing visibility logic
  // handle fallback (pickVisibleTrailDefaults will pick something sensible).
  return null;
}
